import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import useI18n from '../../i18n/useI18n'
import Modal from '../../components/modal'
import { BadgeCellRenderer } from '../../components/table-cell-renderers'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import general from '../../utils/general'

const StudentDetailModal = ({ isOpen, student, onClose, onShowAgentInfo }) => {
  const dispatch = useDispatch()
  const { t } = useI18n()
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(false)

  const activeStudent = details || student
  const user = activeStudent?.user || {}
  const language = activeStudent?.languageProficiency || {}
  const otherFiles = activeStudent?.otherFiles || {}
  const additionalInfo = activeStudent?.additionalInfo || {}

  useEffect(() => {
    if (isOpen && student?.id) {
      setLoading(true)
      dispatch(
        ApiCallActions.Get({
          url: `students/${student.id}`,
          showAlertOnError: true,
          showLoading: false,
          onSuccess: ({ data }) => {
            const payload = data?.data || data || null
            setDetails(payload)
            setLoading(false)
          },
          onError: ({ errorMessage }) => {
            if (errorMessage) {
              general.notificationError(errorMessage)
            }
            setDetails(null)
            setLoading(false)
          }
        })
      )
    }

    if (!isOpen) {
      setDetails(null)
    }
  }, [dispatch, isOpen, student?.id])

  const getFullName = () => {
    const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
    return name || user?.email || t('STUDENTS_ENTITY')
  }

  const resolveAgent = (data) => {
    if (!data) return null
    if (data.agent) return data.agent
    if (Array.isArray(data.agents) && data.agents.length > 0) return data.agents[0]
    return null
  }

  const getAgentNameFromAgentObject = (agent) => {
    if (!agent) return ''
    if (agent.companyName) return agent.companyName
    if (agent.name) return agent.name
    if (agent.fullName) return agent.fullName

    const firstName = agent.firstName || agent?.user?.firstName || ''
    const lastName = agent.lastName || agent?.user?.lastName || ''
    const combined = `${firstName} ${lastName}`.trim()

    if (combined) return combined

    return agent.email || agent?.user?.email || ''
  }

  const agentName = useMemo(() => {
    if (!activeStudent) return ''

    const directName =
      activeStudent.agentName ||
      activeStudent.agentFullName ||
      activeStudent.assignedAgentName ||
      activeStudent.assignedAgentFullName ||
      activeStudent.agentUser?.fullName

    if (directName && typeof directName === 'string' && directName.trim()) {
      return directName.trim()
    }

    const agentUserFirst = activeStudent?.agentUser?.firstName || ''
    const agentUserLast = activeStudent?.agentUser?.lastName || ''
    const agentUserCombined = `${agentUserFirst} ${agentUserLast}`.trim()
    if (agentUserCombined) return agentUserCombined

    const agent = resolveAgent(activeStudent)
    return getAgentNameFromAgentObject(agent)
  }, [activeStudent])

  const agentEntity = useMemo(() => resolveAgent(activeStudent), [activeStudent])
  const agentLogo = useMemo(() => {
    if (!agentEntity) return ''
    return agentEntity.logoUrl || agentEntity.logoURL || agentEntity.logo
  }, [agentEntity])

  const renderInfo = (label, value, icon) => {
    const displayValue =
      value === null || value === undefined || value === '' ? '-' : value

    return (
      <div className="m-b-15 d-flex">
        {icon && <i className={`zmdi ${icon} m-r-10 text-warning`} style={{ fontSize: '18px' }}></i>}
        <div>
          <small className="text-muted d-block">{label}</small>
          <strong>{displayValue}</strong>
        </div>
      </div>
    )
  }

  const renderDocument = (label, url, icon) => (
    <li className="m-b-10">
      <i className={`zmdi ${icon || 'zmdi-attachment-alt'} m-r-5 text-warning`}></i>
      <strong>{label}:</strong>{' '}
      {url ? (
        <a href={url} target="_blank" rel="noreferrer">
          {t('COMMON_VIEW')}
        </a>
      ) : (
        <span className="text-muted">{t('COMMON_NOT_AVAILABLE')}</span>
      )}
    </li>
  )

  const kvkkBadge = (
    <BadgeCellRenderer
      value={!!activeStudent?.kvkkStatus}
      options={{
        trueLabel: t('STUDENTS_TABLE_KVKK_APPROVED'),
        falseLabel: t('STUDENTS_TABLE_KVKK_MISSING'),
        trueClass: 'badge-success',
        falseClass: 'badge-warning'
      }}
    />
  )

  const statusBadge = (
    <BadgeCellRenderer
      value={user?.status === 'active'}
      options={{
        trueLabel: t('STUDENTS_TABLE_STATUS_ACTIVE'),
        falseLabel: t('STUDENTS_TABLE_STATUS_INACTIVE'),
        trueClass: 'badge-success',
        falseClass: 'badge-danger'
      }}
    />
  )

  if (!student) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('STUDENTS_DETAIL_TITLE')}
      subtitle={t('STUDENTS_DETAIL_SUBTITLE')}
      icon="zmdi-account-circle"
      size="xlarge"
    >
      {loading ? (
        <div className="text-center p-4">
          <p>{t('STUDENTS_DETAIL_LOADING')}</p>
        </div>
      ) : (
        <div className="row clearfix">
          <div className="col-lg-4 col-md-5">
            <div className="card">
              <div className="body">
                <div className="m-b-15">
                  <small className="text-muted d-block">{t('STUDENTS_DETAIL_STUDENT_LABEL')}</small>
                  <h5 className="m-b-0">{getFullName()}</h5>
                  <div className="d-flex align-items-center m-t-10" style={{ gap: '8px', flexWrap: 'wrap' }}>
                    {statusBadge}
                    {kvkkBadge}
                  </div>
                </div>

                {renderInfo(t('STUDENTS_DETAIL_FIELDS_EMAIL'), user?.email, 'zmdi-email')}
                {renderInfo(t('STUDENTS_DETAIL_FIELDS_PHONE'), user?.phone, 'zmdi-phone')}
                {renderInfo(
                  t('STUDENTS_DETAIL_FIELDS_AGENT'),
                  agentName ? (
                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                      {agentLogo && (
                        <img
                          src={agentLogo}
                          alt={agentName}
                          style={{ width: 30, height: 30, objectFit: 'contain', borderRadius: 4 }}
                        />
                      )}
                      <span>{agentName}</span>
                     <div
                      className="show-info-btn"
                        title={t('STUDENTS_AGENT_INFO_BUTTON')}
                        onClick={() => onShowAgentInfo && onShowAgentInfo(activeStudent)}
                      >
                        <i className="zmdi zmdi-info-outline" ></i>
                      </div>
                    </div>
                  ) : (
                    <span className="badge badge-info">{t('STUDENTS_TABLE_UNASSIGNED')}</span>
                  ),
                  'zmdi-accounts'
                )}
                {renderInfo(t('STUDENTS_DETAIL_FIELDS_NATIONALITY'), activeStudent?.nationality, 'zmdi-flag')}
                {renderInfo(t('STUDENTS_DETAIL_FIELDS_DATE_OF_BIRTH'), activeStudent?.dateOfBirth, 'zmdi-calendar')}
              </div>
            </div>

            <div className="card">
              <div className="body">
                <h6 className="m-b-10">{t('STUDENTS_DETAIL_ADDITIONAL_INFO_TITLE')}</h6>
                <p className="text-muted" style={{ whiteSpace: 'pre-line' }}>
                  {additionalInfo?.hobbies || t('STUDENTS_DETAIL_ADDITIONAL_INFO_EMPTY')}
                </p>
              </div>
            </div>
          </div>

          <div className="col-lg-8 col-md-7">
            <div className="card">
              <div className="body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="m-b-10">{t('STUDENTS_DETAIL_SECTIONS_ACADEMIC')}</h6>
                    {renderInfo(t('STUDENTS_DETAIL_FIELDS_EDUCATION_LEVEL'), activeStudent?.educationLevel, 'zmdi-graduation-cap')}
                    {renderInfo(t('STUDENTS_DETAIL_FIELDS_GPA'), activeStudent?.gpa ?? '-', 'zmdi-trending-up')}
                    {renderInfo(t('STUDENTS_DETAIL_FIELDS_ENGLISH_LEVEL'), language?.english, 'zmdi-translate')}
                    {renderInfo(t('STUDENTS_DETAIL_FIELDS_TURKISH_LEVEL'), language?.turkish, 'zmdi-translate')}
                  </div>
                  <div className="col-md-6">
                    <h6 className="m-b-10">{t('STUDENTS_DETAIL_SECTIONS_ADDRESS')}</h6>
                    {renderInfo(t('STUDENTS_DETAIL_FIELDS_CITY'), activeStudent?.city, 'zmdi-city')}
                    {renderInfo(t('STUDENTS_DETAIL_FIELDS_COUNTRY'), activeStudent?.country, 'zmdi-globe')}
                    {renderInfo(t('STUDENTS_DETAIL_FIELDS_POSTAL_CODE'), activeStudent?.postalCode, 'zmdi-mail-send')}
                    {renderInfo(t('STUDENTS_DETAIL_FIELDS_ADDRESS'), activeStudent?.address, 'zmdi-pin')}
                  </div>
                </div>

                <hr />

                <div className="row">
                  <div className="col-md-6">
                  <h6 className="m-b-10">{t('STUDENTS_DETAIL_SECTIONS_EMERGENCY')}</h6>
                  {renderInfo(t('STUDENTS_DETAIL_FIELDS_EMERGENCY_NAME'), activeStudent?.emergencyContactName, 'zmdi-account')}
                  {renderInfo(
                    t('STUDENTS_DETAIL_FIELDS_EMERGENCY_PHONE'),
                    activeStudent?.emergencyContactPhone,
                    'zmdi-phone-setting'
                  )}
                  {renderInfo(
                    t('STUDENTS_DETAIL_FIELDS_EMERGENCY_RELATION'),
                    activeStudent?.emergencyContactRelation,
                    'zmdi-accounts-alt'
                  )}
                  </div>
                  <div className="col-md-6">
                  <h6 className="m-b-10">{t('STUDENTS_DETAIL_SECTIONS_DOCUMENTS')}</h6>
                    <ul className="list-unstyled">
                    {renderDocument(
                      t('STUDENTS_DETAIL_DOCUMENTS_PASSPORT'),
                      activeStudent?.passportFile,
                      'zmdi-airline-seat-recline-normal'
                    )}
                    {renderDocument(
                      t('STUDENTS_DETAIL_DOCUMENTS_HIGHSCHOOL_TRANSCRIPT'),
                      activeStudent?.highschoolTranscript,
                      'zmdi-collection-text'
                    )}
                    {renderDocument(
                      t('STUDENTS_DETAIL_DOCUMENTS_HIGHSCHOOL_DIPLOMA'),
                      activeStudent?.highschoolDiploma,
                      'zmdi-assignment-check'
                    )}
                    {renderDocument(t('STUDENTS_DETAIL_DOCUMENTS_CV'), otherFiles?.cv, 'zmdi-file-text')}
                    {renderDocument(
                      t('STUDENTS_DETAIL_DOCUMENTS_MOTIVATION_LETTER'),
                      otherFiles?.motivationLetter,
                      'zmdi-file'
                    )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default StudentDetailModal

